import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { SafeImage } from '@/components/safe-image.tsx';
import { CAROUSEL_INTERVAL_MS } from '@/data/mission.ts';

interface Props {
  /** 보여줄 이미지 주소들. 순서 그대로 돈다 */
  images: (string | null | undefined)[];
  alt?: string;
  /** 장이 여러 개일 때 아래에 점을 찍을지 */
  dots?: boolean;
}

/**
 * 자동으로 넘어가는 이미지 캐러셀.
 *
 * 관리 화면에서 미션 노출 이미지를 여러 장 올릴 수 있고, 여러 장이면 목록 카드와
 * Today PICK 에서 순서대로 돈다. **한 장이면 그냥 이미지 한 장**이다 — 타이머도
 * 점도 만들지 않는다.
 *
 * ★ 겹쳐 놓고 투명도만 바꾼다. 자리를 옮기는 방식(transform)은 카드 크기가
 *   화면마다 달라서 계산이 어긋나기 쉽다.
 *
 * ★ 이미지는 `SafeImage` 로 그린다. DB 에서 온 주소는 파일이 없을 수 있고,
 *   그때 브라우저가 그리는 깨진 아이콘이 카드 전체를 망친다.
 */
export const ImageCarousel: FunctionalComponent<Props> = ({ images, alt = '', dots = true }) => {
  const list = images.filter((src): src is string => !!src);
  const [index, setIndex] = useState(0);

  // 목록이 바뀌면(다른 미션으로 갈아탐) 첫 장부터 다시 돈다. 그러지 않으면
  // 범위를 넘은 자리에 멈춰 아무것도 안 보이는 칸이 생긴다.
  const key = list.join('|');
  useEffect(() => setIndex(0), [key]);

  useEffect(() => {
    if (list.length < 2) return;

    const timer = window.setInterval(
      () => setIndex((i) => (i + 1) % list.length),
      CAROUSEL_INTERVAL_MS
    );
    return () => window.clearInterval(timer);
  }, [list.length]);

  if (list.length < 1) return <SafeImage src={null} alt={alt} />;
  if (list.length === 1) return <SafeImage src={list[0]} alt={alt} />;

  return (
    <span class="carousel">
      {list.map((src, i) => (
        <SafeImage
          key={src}
          src={src}
          alt={i === index ? alt : ''}
          class={i === index ? 'carousel-slide is-on' : 'carousel-slide'}
          aria-hidden={i === index ? undefined : 'true'}
        />
      ))}

      {dots && (
        <span class="carousel-dots">
          {list.map((src, i) => (
            <span key={src} class={i === index ? 'carousel-dot is-on' : 'carousel-dot'} />
          ))}
        </span>
      )}
    </span>
  );
};
